import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useBranding } from '@/contexts/BrandingContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { API } from '@/App';
import LanguageSelector from '@/components/LanguageSelector';
import { analytics } from '@/lib/analytics';
import {
  Users,
  Clock,
  BarChart3,
  Shield,
  Building2,
  Smartphone,
  Globe,
  CheckCircle,
  ArrowRight,
  Star,
  Zap,
  Lock,
  Calendar,
  Coffee,
  FileText,
  Settings,
  Bell,
  UserCheck,
  Timer,
  TrendingUp,
  Layers,
  Monitor,
  ChevronRight,
  Play,
  Download,
  Apple,
  X,
  Check,
  Sparkles,
  BadgeCheck,
  MapPin,
  Camera,
  CreditCard,
  DollarSign,
  Briefcase,
  CalendarDays,
  ClipboardList,
  PlaneTakeoff,
  Wallet,
  ChevronDown,
  HelpCircle,
  MessageCircle
} from 'lucide-react';

import ChatWidgetLoader from '@/components/ChatWidgetLoader';

function LandingPage() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch pricing based on user's location
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/platform/pricing`);
        setPricing(response.data);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Set default pricing on error
        setPricing({
          detected_country: 'Unknown',
          currency: 'USD',
          free_staff_limit: 2,
          price_per_staff: 5,
          features: [
            'pricing_feat_attendance',
            'pricing_feat_checkin',
            'pricing_feat_break',
            'pricing_feat_reports',
            'pricing_feat_mobile',
            'pricing_feat_analytics',
            'pricing_feat_gps',
            'pricing_feat_screenshot',
            'pricing_feat_idle'
          ]
        });
      } finally {
        setPricingLoading(false);
      }
    };
    fetchPricing();
  }, []);

  // Format currency symbol
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹', 'PKR': 'Rs',
      'AED': 'د.إ', 'SAR': 'ر.س', 'CAD': 'C$', 'AUD': 'A$', 'JPY': '¥',
      'CNY': '¥', 'BRL': 'R$', 'MXN': '$', 'SGD': 'S$', 'NZD': 'NZ$'
    };
    return symbols[currency] || currency + ' ';
  };

  const features = [
    { icon: Users, title: t('landing.feat_multi_tenant'), description: t('landing.feat_multi_tenant_desc'), color: 'text-blue-600', bgColor: 'bg-blue-100' },
    { icon: Clock, title: t('landing.feat_realtime'), description: t('landing.feat_realtime_desc'), color: 'text-green-600', bgColor: 'bg-green-100' },
    { icon: Wallet, title: t('landing.feat_payroll'), description: t('landing.feat_payroll_desc'), color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
    { icon: PlaneTakeoff, title: t('landing.feat_leave'), description: t('landing.feat_leave_desc'), color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
    { icon: CalendarDays, title: t('landing.feat_calendar'), description: t('landing.feat_calendar_desc'), color: 'text-violet-600', bgColor: 'bg-violet-100' },
    { icon: Globe, title: t('landing.feat_timezone'), description: t('landing.feat_timezone_desc'), color: 'text-purple-600', bgColor: 'bg-purple-100' },
    { icon: BarChart3, title: t('landing.feat_analytics'), description: t('landing.feat_analytics_desc'), color: 'text-orange-600', bgColor: 'bg-orange-100' },
    { icon: Shield, title: t('landing.feat_rbac'), description: t('landing.feat_rbac_desc'), color: 'text-red-600', bgColor: 'bg-red-100' },
    { icon: Smartphone, title: t('landing.feat_crossplatform'), description: t('landing.feat_crossplatform_desc'), color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
    { icon: MapPin, title: t('landing.feat_gps'), description: t('landing.feat_gps_desc'), color: 'text-teal-600', bgColor: 'bg-teal-100' },
    { icon: Camera, title: t('landing.feat_screenshot'), description: t('landing.feat_screenshot_desc'), color: 'text-pink-600', bgColor: 'bg-pink-100' },
    { icon: Timer, title: t('landing.feat_idle'), description: t('landing.feat_idle_desc'), color: 'text-amber-600', bgColor: 'bg-amber-100' }
  ];

  const detailedFeatures = [
    {
      category: t('landing.role_admin'),
      icon: Building2,
      color: 'from-emerald-500 to-green-600',
      items: [t('landing.admin_feat_1'), t('landing.admin_feat_2'), t('landing.admin_feat_3'), t('landing.admin_feat_4'), t('landing.admin_feat_5'), t('landing.admin_feat_6'), t('landing.admin_feat_7'), t('landing.admin_feat_8'), t('landing.admin_feat_9'), t('landing.admin_feat_10')]
    },
    {
      category: t('landing.role_manager'),
      icon: Briefcase,
      color: 'from-purple-500 to-violet-600',
      items: [t('landing.mgr_feat_1'), t('landing.mgr_feat_2'), t('landing.mgr_feat_3'), t('landing.mgr_feat_4'), t('landing.mgr_feat_5'), t('landing.mgr_feat_6'), t('landing.mgr_feat_7'), t('landing.mgr_feat_8'), t('landing.mgr_feat_9')]
    },
    {
      category: t('landing.role_staff'),
      icon: UserCheck,
      color: 'from-blue-500 to-cyan-600',
      items: [t('landing.staff_feat_1'), t('landing.staff_feat_2'), t('landing.staff_feat_3'), t('landing.staff_feat_4'), t('landing.staff_feat_5'), t('landing.staff_feat_6'), t('landing.staff_feat_7'), t('landing.staff_feat_8'), t('landing.staff_feat_9'), t('landing.staff_feat_10')]
    }
  ];

  const stats = [
    { value: '99.9%', label: t('landing.uptime'), icon: Zap },
    { value: '50+', label: t('landing.features_count'), icon: Layers },
    { value: '24/7', label: t('landing.support'), icon: Clock },
    { value: '100%', label: t('landing.secure'), icon: Lock }
  ];

  const benefits = [
    { title: t('landing.increase_productivity'), description: t('landing.increase_productivity_desc'), icon: TrendingUp },
    { title: t('landing.save_time'), description: t('landing.save_time_desc'), icon: Timer },
    { title: t('landing.better_insights'), description: t('landing.better_insights_desc'), icon: BarChart3 },
    { title: t('landing.stay_compliant'), description: t('landing.stay_compliant_desc'), icon: FileText }
  ];

  const faqs = [
    { question: t('landing.faq_q1'), answer: t('landing.faq_a1') },
    { question: t('landing.faq_q2'), answer: t('landing.faq_a2') },
    { question: t('landing.faq_q3'), answer: t('landing.faq_a3') },
    { question: t('landing.faq_q4'), answer: t('landing.faq_a4') },
    { question: t('landing.faq_q5'), answer: t('landing.faq_a5') },
    { question: t('landing.faq_q6'), answer: t('landing.faq_a6') },
    { question: t('landing.faq_q7'), answer: t('landing.faq_a7') },
    { question: t('landing.faq_q8'), answer: t('landing.faq_a8') },
    { question: t('landing.faq_q9'), answer: t('landing.faq_a9') },
    { question: t('landing.faq_q10'), answer: t('landing.faq_a10') },
    { question: t('landing.faq_q11'), answer: t('landing.faq_a11') },
    { question: t('landing.faq_q12'), answer: t('landing.faq_a12') },
    { question: t('landing.faq_q13'), answer: t('landing.faq_a13') },
    { question: t('landing.faq_q14'), answer: t('landing.faq_a14') }
  ];

  const [openFaq, setOpenFaq] = useState(null);
  const [publicUrls, setPublicUrls] = useState({ social_media: [], download_urls: {} });

  useEffect(() => {
    const fetchPublicUrls = async () => {
      try {
        const response = await axios.get(`${API}/platform/urls`);
        setPublicUrls(response.data);
      } catch (error) {
        console.error('Failed to fetch public URLs:', error);
      }
    };
    fetchPublicUrls();
  }, []);

  const scrollToDownload = (e) => {
    e.preventDefault();
    const section = document.getElementById('download');
    if (section) section.scrollIntoView({ behavior: 'smooth' });
  };

  const SocialIcon = ({ platform }) => {
    const cls = "w-4 h-4 fill-current text-gray-400";
    switch (platform) {
      case 'facebook': return <svg className={cls} viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
      case 'twitter': return <svg className={cls} viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
      case 'linkedin': return <svg className={cls} viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>;
      case 'instagram': return <svg className={cls} viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>;
      case 'youtube': return <svg className={cls} viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
      case 'tiktok': return <svg className={cls} viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>;
      case 'github': return <svg className={cls} viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>;
      case 'whatsapp': return <svg className={cls} viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>;
      default: return <Globe className="w-4 h-4 text-gray-400" />;
    }
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const DownloadModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Download {branding.platform_name}</h2>
            <button 
              onClick={() => setShowDownloadModal(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          
          {/* Mobile Apps */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Mobile Apps
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a 
                href="https://play.google.com/store" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-gray-900 hover:bg-gray-800 rounded-xl text-white transition-colors group"
              >
                <div className="w-12 h-12 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-10 h-10 fill-current">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-400">GET IT ON</p>
                  <p className="text-lg font-semibold">Google Play</p>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              
              <a 
                href="https://apps.apple.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-gray-900 hover:bg-gray-800 rounded-xl text-white transition-colors group"
              >
                <div className="w-12 h-12 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-10 h-10 fill-current">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Download on the</p>
                  <p className="text-lg font-semibold">App Store</p>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>
          
          {/* Desktop Apps */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              Desktop Apps
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <a 
                href="#" 
                className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl text-white transition-all group shadow-lg shadow-blue-500/25"
              >
                <svg viewBox="0 0 24 24" className="w-12 h-12 fill-current">
                  <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                </svg>
                <div className="text-center">
                  <p className="font-semibold">Windows</p>
                  <p className="text-xs text-blue-200">Windows 10+</p>
                </div>
                <Download className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              
              <a 
                href="#" 
                className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 rounded-xl text-white transition-all group shadow-lg shadow-gray-700/25"
              >
                <svg viewBox="0 0 24 24" className="w-12 h-12 fill-current">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-center">
                  <p className="font-semibold">macOS</p>
                  <p className="text-xs text-gray-400">macOS 11+</p>
                </div>
                <Download className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              
              <a 
                href="#" 
                className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-xl text-white transition-all group shadow-lg shadow-orange-500/25"
              >
                <svg viewBox="0 0 24 24" className="w-12 h-12 fill-current">
                  <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18.5c-4.687 0-8.5-3.813-8.5-8.5S7.313 3.5 12 3.5s8.5 3.813 8.5 8.5-3.813 8.5-8.5 8.5zm-3.5-5h7v1.5h-7v-1.5zm0-3h7v1.5h-7v-1.5zm0-3h7v1.5h-7V9.5z"/>
                </svg>
                <div className="text-center">
                  <p className="font-semibold">Ubuntu</p>
                  <p className="text-xs text-orange-200">Ubuntu 20.04+</p>
                </div>
                <Download className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-indigo-50 rounded-xl">
            <p className="text-sm text-indigo-700 text-center">
              <strong>Note:</strong> Mobile and desktop apps are coming soon! Use the web app in the meantime for full functionality.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <ChatWidgetLoader />
      {/* Download Modal */}
      {showDownloadModal && <DownloadModal />}
      
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3">
              {branding.logo_url ? (
                <img 
                  src={branding.logo_url} 
                  alt={branding.platform_name} 
                  className="h-8 md:h-10 object-contain"
                />
              ) : (
                <>
                  <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                    <Building2 className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    {branding.platform_name}
                  </span>
                </>
              )}
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#" className="text-gray-600 hover:text-indigo-600 transition-colors font-medium">{t('landing.about')}</a>
              <a href="#features" className="text-gray-600 hover:text-indigo-600 transition-colors font-medium">{t('landing.features')}</a>
              <a href="#pricing" className="text-gray-600 hover:text-indigo-600 transition-colors font-medium">{t('landing.pricing')}</a>
              <a href="#faq" className="text-gray-600 hover:text-indigo-600 transition-colors font-medium">FAQ</a>
              <button 
                onClick={scrollToDownload}
                className="text-gray-600 hover:text-indigo-600 transition-colors font-medium flex items-center gap-1"
              >
                <Download className="w-4 h-4" />
                {t('reports.download')}
              </button>
              <LanguageSelector compact />
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => {
                  analytics.loginClick('staff');
                  navigate('/login');
                }}
                className="hidden sm:flex"
              >
                {t('auth.staff_login')}
              </Button>
              <Button 
                onClick={() => {
                  analytics.loginClick('admin');
                  navigate('/admin');
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
              >
                {t('auth.admin_login')}
              </Button>
              <div className="md:hidden">
                <LanguageSelector compact />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50" />
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '4s' }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-6">
              <Star className="w-4 h-4" />
              {t('landing.smart_hrm_badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6">
              {t('landing.hero_title').split(' ').slice(0, 2).join(' ')}{' '}
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                {t('landing.hero_title').split(' ').slice(2).join(' ')}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              {t('landing.hero_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                onClick={() => {
                  analytics.getStarted('hero');
                  navigate('/admin');
                }}
                className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all"
              >
                {t('landing.get_started')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => {
                  analytics.downloadClick('section');
                  scrollToDownload();
                }}
                className="w-full sm:w-auto text-lg px-8 py-6 rounded-xl border-2"
              >
                <Download className="mr-2 w-5 h-5" />
                Download App
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">{t('landing.no_credit_card')} • {t('landing.free_forever')}</p>
          </div>

          {/* Stats Bar */}
          <div className="mt-16 md:mt-24">
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 md:p-8 border border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-100 rounded-xl mb-3">
                      <stat.icon className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div className="text-3xl md:text-4xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-gray-500 font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.everything_you_need')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('landing.powerful_features')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature) => (
              <Card key={feature.title} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden">
                <CardContent className="p-6 md:p-8">
                  <div className={`inline-flex items-center justify-center w-14 h-14 ${feature.bgColor} rounded-2xl mb-5 group-hover:scale-110 transition-transform`}>
                    <feature.icon className={`w-7 h-7 ${feature.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Features */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.features_for_every_role')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('landing.features_for_every_role_desc')}
            </p>
          </div>

          <div className="space-y-8">
            {detailedFeatures.map((section, index) => (
              <div 
                key={section.category}
                className={`relative overflow-hidden rounded-3xl bg-gradient-to-r ${section.color} p-1`}
              >
                <div className="bg-white rounded-[22px] p-6 md:p-10">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${section.color} rounded-2xl`}>
                        <section.icon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{section.category}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-20 md:py-32 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {t('landing.download')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-yellow-400">
                {branding.platform_name}
              </span>
            </h2>
            <p className="text-lg text-indigo-200">
              {t('landing.download_desc')}
            </p>
          </div>

          {/* Mobile Apps */}
          <div className="mb-12">
            <h3 className="text-center text-xl font-semibold text-indigo-200 mb-6">{t('landing.mobile_apps')}</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-xl mx-auto">
              <a 
                href={publicUrls.download_urls?.google_play || "https://play.google.com/store"} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full sm:w-auto flex items-center gap-4 px-6 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl text-white transition-colors border border-white/10"
                data-testid="download-google-play"
              >
                <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                </svg>
                <div>
                  <p className="text-xs text-indigo-300">GET IT ON</p>
                  <p className="text-lg font-semibold">Google Play</p>
                </div>
              </a>
              
              <a 
                href={publicUrls.download_urls?.app_store || "https://apps.apple.com"} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full sm:w-auto flex items-center gap-4 px-6 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl text-white transition-colors border border-white/10"
                data-testid="download-app-store"
              >
                <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div>
                  <p className="text-xs text-indigo-300">Download on the</p>
                  <p className="text-lg font-semibold">App Store</p>
                </div>
              </a>
            </div>
          </div>

          {/* Desktop Apps */}
          <div>
            <h3 className="text-center text-xl font-semibold text-indigo-200 mb-6">{t('landing.desktop_apps')}</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-3xl mx-auto">
              <a 
                href={publicUrls.download_urls?.windows || "#"} 
                target={publicUrls.download_urls?.windows ? "_blank" : undefined}
                rel="noopener noreferrer"
                className={`w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-white transition-colors shadow-lg ${publicUrls.download_urls?.windows ? 'bg-blue-500 hover:bg-blue-600 shadow-blue-500/25' : 'bg-blue-400/60 cursor-not-allowed shadow-none'}`}
                data-testid="download-windows"
                onClick={(e) => {
                  if (!publicUrls.download_urls?.windows) e.preventDefault();
                  else analytics.downloadClick('windows');
                }}
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                  <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                </svg>
                <span className="font-semibold">Windows</span>
                <Download className="w-4 h-4" />
              </a>
              
              <a 
                href={publicUrls.download_urls?.macos || "#"} 
                target={publicUrls.download_urls?.macos ? "_blank" : undefined}
                rel="noopener noreferrer"
                className={`w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-white transition-colors shadow-lg ${publicUrls.download_urls?.macos ? 'bg-gray-700 hover:bg-gray-600 shadow-gray-700/25' : 'bg-gray-500/60 cursor-not-allowed shadow-none'}`}
                data-testid="download-macos"
                onClick={(e) => {
                  if (!publicUrls.download_urls?.macos) e.preventDefault();
                  else analytics.downloadClick('macos');
                }}
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span className="font-semibold">macOS</span>
                <Download className="w-4 h-4" />
              </a>
              
              <a 
                href={publicUrls.download_urls?.linux || "#"} 
                target={publicUrls.download_urls?.linux ? "_blank" : undefined}
                rel="noopener noreferrer"
                className={`w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-white transition-colors shadow-lg ${publicUrls.download_urls?.linux ? 'bg-orange-500 hover:bg-orange-600 shadow-orange-500/25' : 'bg-orange-400/60 cursor-not-allowed shadow-none'}`}
                data-testid="download-linux"
                onClick={(e) => {
                  if (!publicUrls.download_urls?.linux) e.preventDefault();
                  else analytics.downloadClick('linux');
                }}
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                  <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 1.5c4.687 0 8.5 3.813 8.5 8.5s-3.813 8.5-8.5 8.5S3.5 16.687 3.5 12 7.313 3.5 12 3.5zM8 15.5h8v-1H8v1zm0-3h8v-1H8v1zm0-3h8v-1H8v1z"/>
                </svg>
                <span className="font-semibold">Ubuntu</span>
                <Download className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-32 bg-gradient-to-b from-white to-indigo-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-200 rounded-full blur-3xl opacity-40" />
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-200 rounded-full blur-3xl opacity-40" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full blur-3xl opacity-30" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-5 py-2 rounded-full text-sm font-bold mb-6 shadow-lg shadow-green-500/30">
              <Sparkles className="w-4 h-4" />
              {t('landing.first_2_free')}
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4">
              {t('landing.one_plan')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('landing.simple_pricing')}
            </p>
          </div>

          {/* Single Pricing Card */}
          {pricingLoading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <div className="relative bg-white rounded-3xl shadow-2xl shadow-indigo-500/20 overflow-hidden border border-indigo-100">
                {/* Gradient top border */}
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                
                <div className="p-8 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 md:gap-12">
                    {/* Left side - Pricing */}
                    <div className="text-center md:text-left">
                      {/* Free badge */}
                      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold mb-6 border border-green-200">
                        <CheckCircle className="w-4 h-4" />
                        {t('landing.free_forever_badge')}
                      </div>
                      
                      {/* Price display */}
                      <div className="mb-6">
                        <div className="flex items-baseline justify-center md:justify-start gap-2">
                          <span className="text-3xl font-bold text-gray-500">
                            {getCurrencySymbol(pricing?.currency)}
                          </span>
                          <span className="text-7xl md:text-8xl font-black text-gray-900">
                            {pricing?.price_per_staff}
                          </span>
                        </div>
                        <p className="text-gray-500 text-lg mt-2">{t('landing.per_staff_month')}</p>
                      </div>

                      {/* Location indicator */}
                      {pricing && pricing.detected_country !== 'Unknown' && (
                        <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full text-sm border border-indigo-200 mb-6">
                          <MapPin className="w-4 h-4" />
                          {t('landing.pricing_for')} {pricing.detected_country}
                        </div>
                      )}

                      {/* CTA Buttons */}
                      <div className="space-y-3">
                        <Button 
                          onClick={() => {
                            analytics.getStarted('pricing');
                            navigate('/admin');
                          }}
                          className="w-full py-6 text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-xl shadow-indigo-500/30 transition-all hover:scale-105"
                        >
                          <Zap className="w-5 h-5 mr-2" />
                          {t('landing.get_started')}
                        </Button>
                        <p className="text-sm text-gray-500 text-center">
                          {t('landing.no_credit_card')} • {t('landing.cancel_anytime')}
                        </p>
                      </div>
                    </div>

                    {/* Right side - Features */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                        {t('landing.everything_included')}
                      </h3>
                      <ul className="space-y-3">
                        {pricing?.features?.map((feature, index) => (
                          <li key={index} className="flex items-center gap-3 group">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform">
                              <Check className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="text-gray-700 font-medium">{t(`landing.${feature}`)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Bottom banner */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-4">
                  <div className="flex flex-wrap items-center justify-center gap-6 text-white text-sm">
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="w-5 h-5" />
                      <span>{t('landing.no_hidden_fees')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      <span>{t('landing.secure_reliable')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      <span>{t('landing.instant_setup')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trust badges */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm mb-4">{t('landing.trusted_by')}</p>
            <div className="flex items-center justify-center gap-8 flex-wrap">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                ))}
                <span className="ml-2 text-gray-600 font-medium">4.9/5 {t('landing.rating')}</span>
              </div>
              <div className="text-gray-600">
                <span className="font-bold text-indigo-600">1000+</span> {t('landing.companies_count')}
              </div>
              <div className="text-gray-600">
                <span className="font-bold text-indigo-600">50,000+</span> {t('landing.staff_tracked')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.why_choose')} {branding.platform_name}?
            </h2>
            <p className="text-lg text-gray-600">
              {t('landing.why_choose_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <Card key={benefit.title} className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features List */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.and_so_much_more')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('landing.and_so_much_more_desc')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { icon: Calendar, label: t('landing.more_shift') },
              { icon: Coffee, label: t('landing.more_break') },
              { icon: Bell, label: t('landing.more_notif') },
              { icon: FileText, label: t('landing.more_csv') },
              { icon: Settings, label: t('landing.more_settings') },
              { icon: Monitor, label: t('landing.more_dashboard') },
              { icon: Lock, label: t('landing.more_auth') },
              { icon: Globe, label: t('landing.more_tz') },
              { icon: Smartphone, label: t('landing.more_mobile') },
              { icon: Users, label: t('landing.more_team') },
              { icon: Timer, label: t('landing.more_time') },
              { icon: TrendingUp, label: t('landing.more_perf') },
              { icon: Wallet, label: t('landing.more_salary') },
              { icon: DollarSign, label: t('landing.more_payment') },
              { icon: PlaneTakeoff, label: t('landing.more_leave') },
              { icon: CalendarDays, label: t('landing.more_holiday') }
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-colors group">
                <item.icon className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                <span className="font-medium text-gray-700 group-hover:text-indigo-600 transition-colors">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 md:py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-6">
              <HelpCircle className="w-4 h-4" />
              {t('landing.got_questions')}
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.faq_title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('landing.faq_desc')}
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between gap-4"
                >
                  <span className="font-semibold text-gray-900 text-lg">{faq.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-indigo-600 flex-shrink-0 transition-transform duration-200 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-indigo-600 to-purple-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('landing.ready_to_transform')}
          </h2>
          <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
            {t('landing.ready_to_transform_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => {
                analytics.getStarted('footer_cta');
                navigate('/admin');
              }}
              className="w-full sm:w-auto bg-white text-indigo-600 hover:bg-gray-100 text-lg px-10 py-6 rounded-xl shadow-lg"
            >
              {t('landing.get_started')}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => {
                analytics.loginClick('staff');
                navigate('/login');
              }}
              className="w-full sm:w-auto text-lg px-10 py-6 rounded-xl border-2 border-white text-white hover:bg-white/10"
            >
              {t('auth.staff_login')}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                {branding.logo_url ? (
                  <img 
                    src={branding.logo_url} 
                    alt={branding.platform_name} 
                    className="h-8 object-contain brightness-0 invert"
                  />
                ) : (
                  <>
                    <div className="p-2 bg-indigo-600 rounded-xl">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-2xl font-bold">{branding.platform_name}</span>
                  </>
                )}
              </div>
              <p className="text-gray-400 max-w-md leading-relaxed">
                {t('landing.footer_desc')}
              </p>
              {branding.contact_email && (
                <p className="text-gray-400 mt-4">
                  Contact: <a href={`mailto:${branding.contact_email}`} onClick={() => analytics.contactClick()} className="text-indigo-400 hover:text-indigo-300">{branding.contact_email}</a>
                </p>
              )}
              {/* Social Media Links */}
              {publicUrls.social_media.length > 0 && (
                <div className="flex items-center gap-3 mt-5" data-testid="footer-social-links">
                  {publicUrls.social_media.map((s) => (
                    <a
                      key={s.platform}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 bg-gray-800 hover:bg-indigo-600 rounded-lg flex items-center justify-center transition-colors"
                      data-testid={`footer-social-${s.platform}`}
                      aria-label={s.platform}
                    >
                      <SocialIcon platform={s.platform} />
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('landing.product')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">{t('landing.features')}</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">{t('landing.pricing')}</a></li>
                <li><a href="#benefits" className="hover:text-white transition-colors">{t('landing.features_count')}</a></li>
                <li><a href="#download" className="hover:text-white transition-colors">{t('reports.download')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('landing.company')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => { analytics.loginClick('admin'); navigate('/admin'); }} className="hover:text-white transition-colors">{t('auth.admin_login')}</button></li>
                <li><button onClick={() => { analytics.loginClick('staff'); navigate('/login'); }} className="hover:text-white transition-colors">{t('auth.staff_login')}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('landing.legal')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">{t('landing.terms_of_service')}</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">{t('landing.privacy_policy')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Trackly.net {t('landing.all_rights_reserved')}</p>
            <div className="mt-2 flex justify-center gap-4">
              <Link to="/terms" className="hover:text-white transition-colors">{t('landing.terms')}</Link>
              <span>|</span>
              <Link to="/privacy" className="hover:text-white transition-colors">{t('landing.privacy')}</Link>
            </div>
            {branding.website && (
              <p className="mt-2">
                <a href={branding.website} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300">
                  {branding.website}
                </a>
              </p>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
